import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { Star, Award, ShieldCheck, MapPin, Trophy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Rankings() {
  const { service, province } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const prettyProv = (province || "").replace(/-/g, " ");

  useEffect(() => {
    setLoading(true);
    api.get(`/rankings/${service}/${encodeURIComponent(province)}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [service, province]);

  useEffect(() => {
    if (!data) return;
    // SEO: dynamic title + description
    document.title = `Top ${data.pros.length} de ${data.service_name} en ${data.province} · MarinaHub`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    meta.content = `Los mejores profesionales de ${data.service_name.toLowerCase()} en ${data.province}, verificados y con reseñas reales. Recibe presupuestos gratis en MarinaHub.`;
  }, [data]);

  if (loading) return <div className="min-h-screen hub-cream"><Navbar /><div className="p-20 text-center text-[#5C6B7D]">Cargando ranking...</div></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen hub-cream">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        <div className="hub-pill hub-sand text-[#0B1D3A] mb-3 inline-flex"><Trophy size={12}/> Ranking oficial MarinaHub</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0B1D3A] tracking-tight">
          Los mejores {data.service_name.toLowerCase()} en {data.province}
        </h1>
        <p className="text-[#5C6B7D] mt-3 max-w-2xl">
          Actualizado {new Date(data.generated_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}.
          Ranking basado en {data.total > 0 ? `${data.total} profesionales activos, calculado a partir de` : ""} reseñas verificadas, trabajos completados y verificación KYC.
        </p>

        {data.pros.length === 0 ? (
          <div className="hub-card p-10 mt-8 text-center">
            <p className="text-[#5C6B7D] mb-4">Aún no hay profesionales de este servicio en {data.province}.</p>
            <p className="text-sm text-[#5C6B7D] mb-6">Publica tu solicitud y los primeros que se registren te ofertarán.</p>
            <Link to="/register?role=owner"><Button className="hub-btn-primary rounded-full gap-2">Publicar mi solicitud <ArrowRight size={14}/></Button></Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {data.pros.map((p, i) => {
              const prof = p.professional || {};
              const featured = (prof.featured_until || "") > new Date().toISOString();
              return (
                <Link key={p.id} to={`/pro/profile/${p.id}`} className="block" data-testid={`rank-${i+1}`}>
                  <div className={`hub-card p-5 md:p-6 hover:shadow-lg transition-shadow ${featured ? "border-[#FF6B57] border-2" : ""}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-serif text-2xl shrink-0 ${i === 0 ? "bg-[#FF6B57] text-white" : i < 3 ? "bg-[#0B1D3A] text-white" : "bg-[#EFE9E1] text-[#0B1D3A]"}`}>
                        {i + 1}
                      </div>
                      {prof.photo_path ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${prof.photo_path}`} alt={p.name} className="w-14 h-14 rounded-full object-cover"/>
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-white border border-[#0B1D3A]/10 flex items-center justify-center font-serif text-2xl text-[#0B1D3A]">
                          {p.name?.[0] || "?"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="font-display font-semibold text-lg text-[#0B1D3A] truncate">{p.name}</h2>
                          {prof.kyc_status === "verified" && <span className="hub-pill bg-emerald-100 text-emerald-800 text-[10px]"><ShieldCheck size={9}/> KYC</span>}
                          {featured && <span className="hub-pill bg-[#FF6B57] text-white text-[10px]">Destacado</span>}
                        </div>
                        <div className="text-xs text-[#5C6B7D] mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><Star size={11} className="fill-[#FF6B57] text-[#FF6B57]"/><b className="text-[#0B1D3A]">{(prof.rating || 0).toFixed(1)}</b> ({prof.reviews_count || 0})</span>
                          <span className="flex items-center gap-1"><Award size={11}/> {prof.jobs_completed || 0} trabajos</span>
                          {prof.years_experience > 0 && <span>🎯 {prof.years_experience} años</span>}
                        </div>
                        {prof.bio && <p className="text-sm text-[#5C6B7D] mt-2 line-clamp-1">{prof.bio}</p>}
                      </div>
                      <ArrowRight size={16} className="text-[#5C6B7D] shrink-0"/>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-10 hub-navy rounded-2xl p-8 text-center">
          <h3 className="font-serif text-3xl text-white">¿Tu barco necesita {data.service_name.toLowerCase()}?</h3>
          <p className="text-white/70 mt-2">Publica tu solicitud gratis y los profesionales del ranking te enviarán ofertas en minutos.</p>
          <Link to="/register?role=owner"><Button className="hub-btn-coral hub-btn-primary mt-5 gap-2 rounded-full">Publicar solicitud <ArrowRight size={14}/></Button></Link>
        </div>
      </main>
    </div>
  );
}
